/**
 * AES-256-CBC 对称加密工具（基于 Web Crypto API，无需额外依赖）
 *
 * 加密方案：
 *   - 算法：AES-256-CBC
 *   - 密钥：来自 VITE_CRYPTO_KEY 环境变量（64 位十六进制 = 32 字节）
 *   - IV：每次随机生成 16 字节，与密文一同 Base64 编码传输
 *   - 格式：Base64(IV[16字节] + 密文)
 *
 * 仅在 VITE_CRYPTO_ENABLED=true 时对 Prod 环境生效
 */

export const CRYPTO_ENABLED = import.meta.env.VITE_CRYPTO_ENABLED === 'true'
export const CRYPTO_REQUEST_HEADER = 'x-crypto-enabled'
const CRYPTO_KEY_HEX = import.meta.env.VITE_CRYPTO_KEY as string | undefined

// ——— 私有工具函数 ———

function hexToBytes(hex: string): Uint8Array<ArrayBuffer> {
  if (!hex || hex.length % 2 !== 0) {
    throw new Error('[Crypto] 无效的十六进制密钥，请检查 VITE_CRYPTO_KEY')
  }
  // 显式使用 ArrayBuffer 而非 ArrayBufferLike，满足 Web Crypto API 的 BufferSource 类型要求
  const buffer = new ArrayBuffer(hex.length / 2)
  const bytes = new Uint8Array(buffer)
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.slice(i, i + 2), 16)
  }
  return bytes
}

let _cryptoKeyPromise: Promise<CryptoKey> | null = null

function getKey(): Promise<CryptoKey> {
  if (!CRYPTO_KEY_HEX) {
    return Promise.reject(new Error('[Crypto] VITE_CRYPTO_KEY 未配置'))
  }
  if (!_cryptoKeyPromise) {
    _cryptoKeyPromise = crypto.subtle.importKey(
      'raw',
      hexToBytes(CRYPTO_KEY_HEX),
      { name: 'AES-CBC' },
      false,
      ['encrypt', 'decrypt'],
    )
  }
  return _cryptoKeyPromise
}

// ——— 公开 API ———

/**
 * 加密任意数据，返回 Base64 字符串
 * 格式：Base64(IV[16] + CipherText)
 */
export async function encrypt(data: unknown): Promise<string> {
  const key = await getKey()
  const iv = crypto.getRandomValues(new Uint8Array(16))
  const encoded = new TextEncoder().encode(JSON.stringify(data))
  const encrypted = await crypto.subtle.encrypt({ name: 'AES-CBC', iv }, key, encoded)

  const combined = new Uint8Array(16 + encrypted.byteLength)
  combined.set(iv, 0)
  combined.set(new Uint8Array(encrypted), 16)

  // 大数组转 Base64（避免 btoa 栈溢出）
  let binary = ''
  combined.forEach((b) => (binary += String.fromCharCode(b)))
  return btoa(binary)
}

/**
 * 解密 Base64 字符串，返回原始数据
 */
export async function decrypt<T = unknown>(cipherText: string): Promise<T> {
  const key = await getKey()
  const combined = Uint8Array.from(atob(cipherText), (c) => c.charCodeAt(0))
  const iv = combined.slice(0, 16)
  const encrypted = combined.slice(16)

  const decrypted = await crypto.subtle.decrypt({ name: 'AES-CBC', iv }, key, encrypted)
  return JSON.parse(new TextDecoder().decode(decrypted)) as T
}
